import { useState } from 'react';
import { View } from 'react-native';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { LayoutBreakpoints } from '@/constants/LayoutBreakpoints';
import { useIsWidthUp } from '~/global/hooks/useIsWidthUp';
import { Icon } from '~/global/components/icon/Icon';
import { useDeleteProtection } from '~/global/hooks/useDeleteProtection';
import { useConfirmation } from '~/global/hooks/useConfirmation';
import { useAuth } from '~/auth/contexts/auth/Auth';
import { usePurchases } from '~/purchases/contexts/purchases/Purchases';
import { DeleteCurrentUserDocument } from '~/graphql/generated/graphql';
import { useColor } from '~/global/hooks/useColor';

/** The backend rejects deletion with this code while a subscription still renews. */
const ACTIVE_SUBSCRIPTION_ERROR = 'USER__ACTIVE_SUBSCRIPTION';

export const DangerZoneCard = () => {
  const { t } = useTranslation('user');
  const { handleLogout } = useAuth();
  const router = useRouter();
  const destructiveColor = useColor('destructive');
  const isDesktop = useIsWidthUp(LayoutBreakpoints.desktop);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { hasRenewingSubscription, openManageSubscriptions, refresh } =
    usePurchases();

  const [deleteUser, { loading: deleting }] = useMutation(
    DeleteCurrentUserDocument,
  );

  // Shown when the user still has an auto-renewing subscription: they must cancel it
  // at the store before the account can be deleted.
  const { dispatchConfirmationDialog: dispatchManageSubscription } =
    useConfirmation(openManageSubscriptions, {
      title: t('settings.dangerZone.subscriptionActiveTitle'),
      description: t('settings.dangerZone.subscriptionActiveDescription'),
      confirmText: t('settings.dangerZone.manageSubscription'),
    });

  const handleDeleteAccount = async () => {
    try {
      await deleteUser();
      await handleLogout();
      router.replace('/');
    } catch (error) {
      // Backend backstop: if a subscription is still renewing (stale client state),
      // re-sync and route the user to cancel it instead of failing silently.
      if (
        error instanceof Error &&
        error.message.includes(ACTIVE_SUBSCRIPTION_ERROR)
      ) {
        await refresh();
        dispatchManageSubscription();
        return;
      }
      console.error('Failed to delete account:', error);
    }
  };

  const { dispatchConfirmationDialog } = useDeleteProtection(
    handleDeleteAccount,
    {
      description: t('settings.dangerZone.deleteWarning'),
    },
  );

  // Route the delete action: cancel-subscription dialog when one is still renewing,
  // otherwise the normal delete confirmation.
  const requestDelete = () =>
    hasRenewingSubscription
      ? dispatchManageSubscription()
      : dispatchConfirmationDialog();

  return (
    <View className="bg-surface rounded-2xl p-4 xl:p-6 gap-4">
      <Text className="text-sm xl:text-base font-semibold text-destructive">
        {t('settings.dangerZone.title')}
      </Text>

      {/* Mobile vs desktop variants are conditionally rendered (not toggled with
          `display:none` via `xl:hidden`/`hidden xl:flex`): a re-rendering
          phone-`display:none` node crashes iOS New Arch (RN #52349). */}
      {!isDesktop &&
        (!showConfirmation ? (
          <View>
            <Button
              onPress={() =>
                hasRenewingSubscription
                  ? dispatchManageSubscription()
                  : setShowConfirmation(true)
              }
              variant="ghost"
              className="flex-row items-center justify-center gap-2 rounded-lg border border-destructive bg-destructive/10 py-3.5"
              textClassName="text-destructive"
            >
              <Icon name="Trash2" size={18} color={destructiveColor} />
              <Text className="text-sm font-semibold text-destructive">
                {t('settings.dangerZone.deleteAccount')}
              </Text>
            </Button>
          </View>
        ) : (
          <View className="gap-3 p-4 border border-destructive rounded-lg bg-destructive/10">
            <Text className="text-sm font-medium text-destructive">
              {t('settings.dangerZone.confirmDelete')}
            </Text>
            <Text className="text-sm text-muted-foreground">
              {t('settings.dangerZone.deleteWarning')}
            </Text>
            <View className="flex-row gap-2 mt-2">
              <Button
                onPress={requestDelete}
                variant="destructive"
                loading={deleting}
                className="flex-1"
              >
                <Text>{t('settings.dangerZone.confirmButton')}</Text>
              </Button>
              <Button
                onPress={() => setShowConfirmation(false)}
                variant="secondary"
                className="flex-1"
              >
                <Text>{t('settings.dangerZone.cancelButton')}</Text>
              </Button>
            </View>
          </View>
        ))}

      {isDesktop && (
        <View className="flex-row items-center justify-between">
          <View className="gap-1">
            <Text className="text-sm font-medium text-foreground">
              {t('settings.dangerZone.deleteAccount')}
            </Text>
            <Text className="text-xs text-text-muted">
              {t('settings.dangerZone.deleteDescription')}
            </Text>
          </View>
          <Button
            onPress={requestDelete}
            variant="ghost"
            loading={deleting}
            className="flex-row items-center gap-2 rounded-lg border border-destructive bg-destructive/10 px-4 py-2.5"
            textClassName="text-destructive"
          >
            <Icon name="Trash2" size={16} color={destructiveColor} />
            <Text className="text-[13px] font-semibold text-destructive">
              {t('settings.dangerZone.delete')}
            </Text>
          </Button>
        </View>
      )}
    </View>
  );
};
